/**
 * Every magic number the HUD, callouts, meter and RESULT screen use.
 * ARCHITECTURE.md §4.11 — one frozen object, findable in one place.
 *
 * All layout coordinates are in the 1920x1080 DESIGN space. `ui.js` scales that space to
 * the real viewport, so nothing here ever needs a viewport query.
 *
 * All durations are SECONDS OF SIMULATION TIME (ctx.time.sim). Nothing in this subsystem
 * reads a wall clock, which is what makes the shots bit-reproducible (ARCHITECTURE.md §7.3).
 */

export const TUNING = Object.freeze({
  /** Design canvas the HUD is authored against. */
  design: Object.freeze({ width: 1920, height: 1080 }),

  /** Backing-store multiplier cap for the HUD canvases (crispness vs fill cost). */
  maxCanvasScale: 2,

  /**
   * The HUD's protected zone. `combo-hud` rubric criterion 7: x 0.25–0.75 / y 0.20–0.80 must
   * be free of screen-space HUD. Everything corner-anchored is authored against these.
   */
  safe: Object.freeze({
    /** No HUD ink right of this on the left column (design px, 0.24 W). */
    leftColRight: 460,
    /** No HUD ink left of this on the right column (design px, 0.75 W). */
    rightColLeft: 1440,
    /** Right-hand margin the right column is flush against. */
    rightMargin: 20,
    /** Right column must terminate above this (0.30 H). */
    rightColBottom: 324,
  }),

  // ---------------------------------------------------------------- timer (top-left)
  timer: Object.freeze({
    box: Object.freeze({ x: 24, y: 10, w: 460, h: 290 }),
    /** Cap height of the huge countdown numeral, design px. 0.141 x H (rubric floor 0.11). */
    capH: 152,
    weight: 0.265,          // stroke weight in cap-height units — the "fat" of the numerals
    tracking: 0.015,
    skewDeg: 11,
    digitsX: 34,
    baselineY: 190,
    outline: 0.052,         // black outline, cap-height units → 7.9 px = 0.73 % of H
    /**
     * Hard offset in the SAME ink as the outline. reference/REFERENCE.md §2.1: the outline is
     * "offset very slightly down-right so it reads as a shadow as well as a stroke". Opaque,
     * zero blur — it is an outline offset, not a soft drop shadow.
     */
    shadow: Object.freeze({ dx: 4, dy: 5, blur: 0, alpha: 1 }),
    /**
     * §2.2 — LOWERCASE, one quarter of the digit height, one line only, French only.
     *
     * Round 8 measured the x-height at 0.168 of the digit height against the 0.25 the
     * criterion and the reference both want, i.e. it read as a caption rather than as the
     * nameplate for the number above it. At `size` 56 the x-height lands at ~29 px (0.24 of
     * the 122 px rendered digit height) and the cap height at ~40 px = 0.26 of it. `x` now
     * matches `digitsX` exactly so the two share a left edge, as §2.2 specifies.
     */
    label: Object.freeze({ text: 'temps', x: 34, y: 248, size: 56, tracking: 4, squeeze: 1.0 }),
    /** Below this many seconds the numeral pulses. */
    warnAt: 15,
    /** Below this it goes red and pulses hard. */
    criticalAt: 8,
    pulseHz: 2.6,
    criticalPulseHz: 5.0,
    pulseScale: 0.085,
    criticalPulseScale: 0.15,
    /** Below this point in the critical pulse cycle the numeral blinks fully off. */
    blinkBelow: 0.18,
    /** Fractional seconds are shown below this remaining time (adds urgency). */
    decimalsBelow: 10,
  }),

  /**
   * "+10 SEC" token. Anchored to the timer — it rises OUT of the numerals and fades, rather
   * than being a second free-floating yellow block of the same weight competing with them.
   * It lives on its own small canvas so the big timer layer does not repaint every frame
   * while a bonus is flying (ui.update p95 budget, ARCHITECTURE.md §9).
   */
  timeBonus: Object.freeze({
    box: Object.freeze({ x: 24, y: 10, w: 540, h: 210 }),
    x: 296, baselineY: 156,
    capH: 58, weight: 0.24, skewDeg: 11,
    labelSize: 24,
    riseY: 62,              // design px it floats up over its life
    life: 2.1,              // seconds
    popT: 0.26,             // overshoot portion
    holdT: 0.55,            // fraction of life at full opacity
    /** Seconds the sticky (harness-forced) popup is pinned at. */
    heroT: 0.42,
    maxLive: 6,
  }),

  /**
   * ONE physical signboard in the top-right: marquee → cash → marquee → points → objective.
   * Left edge sits on the protected-centre boundary (x = 0.75 W) and the whole unit
   * terminates above y = 0.30 H, so no HUD ink crosses the road vista or the right kerb.
   */
  plate: Object.freeze({
    box: Object.freeze({ x: 1440, y: 12, w: 460, h: 272 }),
    pad: 0,

    /**
     * ------------------------------------------------------------------ ONE SIGNBOARD
     *
     * reference/REFERENCE.md §2.3: the top-right block is a SINGLE physical signboard —
     * "three stacked strips inside a single red frame", cream tiles, ink letters, a round red
     * lamp at each end of a strip. Round 8's critic measured what we actually shipped: five
     * detached rounded pills in three different material languages (cream-and-red marquee
     * strips, a navy strip with yellow type, a black strip with an orange meter), each with
     * its own outline and a gap between them — "not the reference's single physical
     * signboard", and the organising idea of §2.0 broken.
     *
     * So the geometry below describes ONE object: an ink border, a red frame, and five
     * windows cut into that frame. There are no per-row outlines and no inter-row gaps —
     * every seam you see is the frame showing through, which is what makes a letterboard
     * read as a letterboard. Rows are absolute Y offsets inside `box` so the stack can never
     * drift apart again.
     */
    board: Object.freeze({
      r: 13,                 // outer corner radius
      ink: 5,                // black border, all four sides
      frame: 9,              // red frame between the border and the first window
      seamY: 5,              // red seam BETWEEN windows — the only vertical separation
      bevel: 4,              // highlight band along the top of the frame
      seam: 3,               // vertical tile seam width, shared by every row
    }),

    /**
     * Strip 1 — §2.3's letterboard: "individual dark letters on individual cream tiles",
     * ONE letter per tile, with a round red lamp at each end. `ARGENT TOTAL` is the French
     * of the reference's own `TOTAL EARNED` and is the same 12 characters long, so the tile
     * pitch matches the reference strip rather than leaving half the board blank.
     */
    marquee: Object.freeze({ y: 14, h: 32, tiles: 12, bulbR: 8, labelSize: 22, tracking: 0 }),
    /**
     * Strip 2 — the running total. §2.4/`combo-hud` criterion 4: navy on cream, right
     * aligned, ONE GLYPH PER TILE CELL. `$` takes cell 0; the digits fill the rest from the
     * right. Round 8: "the money plate loses its tile cells exactly where the digits are".
     */
    cash: Object.freeze({ y: 51, h: 58, cells: 9, capH: 40, weight: 0.21, label: 'ARGENT TOTAL' }),
    /** Strip 3 — the short word, centred on the same tile pitch (the reference's `FARE`). */
    scoreMarquee: Object.freeze({ y: 114, h: 28, tiles: 12, bulbR: 6, labelSize: 19 }),
    /**
     * Strip 4 — points. Same cream tiles and the same navy digits as the money row: the navy
     * pill with yellow type WAS the second material language on the board. Yellow now belongs
     * exclusively to the timer and the transient callouts, which is the hierarchy we want.
     */
    score: Object.freeze({
      y: 147, h: 50, tiles: 12, capH: 30, weight: 0.21, label: 'POINTS',
      /** Digits fill cells [multCells, tiles-1] from the right; the multiplier owns the rest. */
      multCells: 5,
    }),
    /** Multiplier lives here and ONLY here, in the frame's own maroon — no new hue. */
    mult: Object.freeze({ capH: 22, weight: 0.22 }),
    /**
     * Strip 5 — objective progress. The bar IS a tile run: one cell per pothole, filled cells
     * in works orange, empty cells in the same cream as every other strip. That keeps the
     * board one object instead of bolting a black meter to the bottom of it.
     */
    progress: Object.freeze({
      y: 202, h: 56, labelH: 26, gap: 3, label: 'RÉPARÉS',
      /** 14 cells: 'RÉPARÉS' cell-set from 0, the count cell-set from the right. No overlap. */
      tiles: 14, labelSize: 20, countCapH: 22, maxCells: 24,
    }),
    /**
     * Night. §14 gap 7 concedes there is no Crazy Taxi night precedent, so the value drop is
     * a judgement call — but round 8 was explicit that DESATURATING was the wrong direction:
     * "drop the cream in value while keeping its chroma, target roughly #C9C0A8". R−B is 33
     * here against 14 before, so the plate stays a warm enamel object in an amber frame.
     */
    nightFace: '#C9C0A8',
    nightFaceLow: '#AFA791',
    nightFrame: '#7C2A2C',
    /** Seconds an odometer takes to roll to a new value. */
    rollSeconds: 0.9,
    /** A jump bigger than this rolls; smaller ones snap (keeps small ticks readable). */
    rollMin: 1,
  }),

  /**
   * ---------------------------------------------------------------- combo callout
   * ONE transient callout, right-aligned, inside the 0.27–0.36 vertical band
   * (`combo-hud` rubric criterion 5). The headline is at most TWO tokens and its left edge
   * is clamped so it can never reach x = 0.76 W.
   */
  callout: Object.freeze({
    box: Object.freeze({ x: 1440, y: 288, w: 460, h: 106 }),
    /** Right-aligned pen, local to the box → absolute 1834 = 0.955 W. */
    anchorX: 394,
    /** Hard left limit for callout ink, local to the box → absolute 1459 = 0.760 W. */
    minX: 19,
    baseline: 82,
    /** "COMBO" token. */
    labelSize: 44,
    labelTracking: 4,
    labelGap: 18,
    /** "×8" token. ~2x the label, per §2.7. */
    comboCapH: 78,
    comboWeight: 0.24,
    /** Outline widths: 0.5 % of frame height. */
    labelOutline: 6,
    numOutline: 0.07,
    /** Text-only callouts (no live chain) shrink to fit rather than growing leftward. */
    textSize: 50,
    textOutline: 6,
    /**
     * `gameplay` already puts a world-space callout over the truck on every slalom link, so
     * the corner banner only speaks on every Nth link rather than duplicating it.
     */
    slalomBannerEvery: 4,
    /**
     * §2.7 — every transient callout leans forward. The reference band is 8-12°; this is
     * matched to the timer's 11° so the whole yellow-on-ink family reads as one type system
     * while the marquee lettering stays upright.
     */
    skewDeg: 11,
    life: 2.4,
    popT: 0.30,             // overshoot phase length in seconds
    fadeT: 0.45,            // fade-out tail in seconds
    overshoot: 0.22,        // extra scale at the peak of the pop
    /** Where a harness-forced (sticky) callout is pinned on its own timeline. */
    heroT: 0.44,
    /** Slight settle wobble amplitude after the pop. */
    wobble: 0.030,
    wobbleHz: 3.2,
  }),

  // ---------------------------------------------------------------- destination card
  card: Object.freeze({
    box: Object.freeze({ x: 26, y: 330, w: 412, h: 288 }),
    thumb: Object.freeze({ x: 16, y: 14, w: 268, h: 160 }),
    nameSize: 34,
    nameY: 218,
    distCapH: 34,
    distWeight: 0.2,
    distY: 266,
    frame: 5,
  }),

  // ---------------------------------------------------------------- speed + gear
  drive: Object.freeze({
    box: Object.freeze({ x: 1348, y: 838, w: 552, h: 232 }),
    speedCapH: 96,
    speedWeight: 0.22,
    speedX: 352,
    speedBaseline: 150,
    unitSize: 32,
    gear: Object.freeze({ x: 382, y: 74, w: 140, h: 106, r: 18, size: 80 }),
    /** km/h above which the speed numerals go hot. */
    hotKmh: 85,
  }),

  // ---------------------------------------------------------------- repair meter
  meter: Object.freeze({
    /**
     * The panel is AUTHORED in `design` px and drawn through a single `scale` transform, so
     * `box` is just `design * scale` (560x170 at 0.80 = 448x136).
     *
     * ------------------------------------------------------------------ WHY IT SHRANK
     * Round 8, `pothole-fill`: "the HUD runs two competing completion meters and puts the
     * denser one in the bottom centre… the mosaic is hard to parse at a glance. Reference
     * grammar keeps the four corners loaded and the middle clear."
     *
     * Both halves of that are now fixed. The 12x3 coverage mosaic is GONE — it was our
     * invention, not PowerWash's; §5.1 measures the real completion HUD as exactly four
     * things: a circular % badge, an object-name plate, a ~4 px segmented bar immediately
     * under the name, and a star row. That is what this draws now, and nothing else. And the
     * whole unit moved out of the bottom centre into the bottom-LEFT corner, so the run
     * progress (RÉPARÉS, top-right) and the hole progress (% REMPLI, bottom-left) sit in two
     * different corners instead of arguing across the middle of the road.
     */
    design: Object.freeze({ w: 560, h: 170 }),
    scale: 0.80,
    box: Object.freeze({ x: 26, y: 916, w: 448, h: 136 }),
    ring: Object.freeze({ cx: 92, cy: 86, r: 58, thickness: 20, gapDeg: 62 }),
    pctCapH: 42, pctWeight: 0.2,
    pctLabelSize: 20,
    stars: Object.freeze({ cx: 246, y: 130, spacing: 34, r: 13 }),
    title: Object.freeze({ x: 178, y: 62, size: 32 }),
    bar: Object.freeze({ x: 178, y: 80, w: 356, h: 20, segments: 20, seam: 3 }),
    /** Seconds the ring lags the true fill (so it reads as motion, not a snap). */
    smooth: 0.14,
    /**
     * ---------------------------------------------------------------- TRACKING THE TRUE FILL
     * See `_syncRepair` for the full derivation. Two numbers, doing two different jobs:
     *
     * `relatchEps` — how far the target must move before the smoothing origin is re-stamped.
     * It exists because the old guard re-stamped on ANY change, which pinned the ease at t = 0
     * forever and froze the readout at 0.052 for 211 of 240 frames of a 3.6 s repair.
     * At 0.006 and a normal 0.28/s fill the origin moves every ~21 ms, which is ~15 % of
     * `smooth` — enough elapsed time for the ease to do real work, short enough that the
     * readout never visibly steps.
     *
     * `maxLag` — the absolute error bound, in fill units. `relatchEps` alone leaves the lag
     * proportional to the fill RATE (steady state is `eps / outCubic(eps / rate / smooth)`):
     * 1.4 pp at 0.28/s, but 4.9 pp if gameplay ever fills a hole in one second. Clamping the
     * result makes the 3 pp criterion hold at any rate instead of at the rate we happened to
     * measure. It also means a shot taken after `step(n)` with no intervening `update()` can
     * never render a stale meter.
     */
    relatchEps: 0.006,
    maxLag: 0.02,
    /** 100% snap: ring flashes white and the badge punches. */
    snapDur: 0.55,
    snapScale: 0.30,
    /** Fade in/out of the whole meter, seconds. */
    fade: 0.18,
  }),

  // ---------------------------------------------------------------- objective chevron
  chevron: Object.freeze({
    size: 260,
    /**
     * Redraw budget: the glyph is a pure function of the distance-driven scale, the
     * on/off-screen flag and the edge angle. Bob and position are applied as a CSS transform,
     * so the canvas only repaints when one of those three quantises to a new value.
     */
    scaleQuantum: 0.02,
    angleQuantum: 0.05,
    /** Radius of the ring the chevron is clamped to when the target is off-screen. */
    edgeInset: 190,
    /** Bob amplitude and rate when the target is on-screen. */
    bobPx: 16, bobHz: 1.35,
    /** Scale at the near/far distance limits. */
    nearDist: 12, farDist: 140,
    nearScale: 0.98, farScale: 0.50,
    /** Distance under which the chevron switches from "go there" to "you are here". */
    arrivedDist: 9,
    /**
     * How far above the objective the chevron floats, near and far, design px. `liftNear` is
     * sized to clear the truck cab entirely — at close range the objective projects on top of
     * the hopper, and an arrow sitting on the vehicle occludes the thing the player steers.
     */
    liftNear: 366, liftFar: 120,
    /** Off-screen the chevron is a direction cue, not a marker, so it shrinks. */
    offScreenScale: 0.72,
    /**
     * reference/REFERENCE.md §2.6: the arrow is NOT a marker pinned to the objective, it is a
     * 3-D chevron hovering over the road ahead, vertical centre 0.10–0.16 H, horizontal
     * centre 0.48–0.53 W. Pinning it to the objective is what parked it on the truck hopper
     * in `winter-slush`, casting a shadow on the vehicle the player is steering.
     *
     * So: x still tracks the objective (that is the whole information content), y is clamped
     * into the reference band and x is kept off both corner columns. Design px.
     */
    bandTop: 108, bandBottom: 160,
    safeLeft: 480, safeRight: 480,
    /** Stand-in objective used only while `gameplay` has not published one (harness only). */
    previewDist: 46, previewLateral: 3.2,
  }),

  // ---------------------------------------------------------------- vignette + shakes
  vignette: Object.freeze({
    thumpStrength: 0.92, thumpDecay: 0.62,
    crashStrength: 0.7, crashDecay: 0.55,
    comboBreakStrength: 0.5, comboBreakDecay: 0.7,
    /** Constant red breathing when the clock is critical. */
    criticalBase: 0.24, criticalHz: 2.4,
  }),

  // ---------------------------------------------------------------- RESULT screen
  result: Object.freeze({
    /** Timeline, seconds from the moment phase became 'result'. Everything settles by 1.75. */
    t: Object.freeze({
      backdrop: 0.00, backdropDur: 0.22,
      banner: 0.08, bannerDur: 0.42,
      cash: 0.34, cashRoll: 0.72,
      score: 0.50, scoreRoll: 0.72,
      chips: 0.72, chipStagger: 0.09, chipDur: 0.34,
      cards: 0.90, cardDur: 0.40,
      stamp: 1.14, stampDur: 0.26, stampShake: 0.44,
      /**
       * rubric criterion 3: the completion beat is a SIMULTANEOUS burst, so the stars land on
       * the same frame the badge arc starts sweeping (`cards`), staggered only inside the row.
       */
      stars: 0.90, starStagger: 0.07, starDur: 0.30,
      prompt: 1.52, promptDur: 0.22,
    }),
    /**
     * ONE drop-shadow vector and ONE colour for every plate and every piece of display type on
     * this panel. Round 6 shipped five different offsets (banner 10/12, body 8/10, chip 5/6,
     * card 6/7, title 7/8) which read as three light sources; the critic called it out.
     */
    shadow: Object.freeze({ dx: 7, dy: 9, typeAlpha: 0.9 }),

    /**
     * Banner geometry.
     *
     * `titleBaselineY` is now EXPLICIT rather than a fraction of the banner height, because
     * the constraint that matters is accent clearance, not proportion: an acute on a capital
     * É rises to ~0.95 of the font size above the baseline, so the banner's top edge must sit
     * at least that far above the baseline plus 0.15 cap-height of air. Round 6 set the
     * baseline at 0.78·h with size 150 in a 178-tall banner, which put the accent 11 px ABOVE
     * the banner and sheared it flat.
     *
     *   clearance = titleBaselineY - accentRise·titleSize - y
     *             = 168 - 0.95·122 - 34 = 18.1 px = 0.21 cap-height.   ✓
     *
     * The same sum runs a second time between the title's drop shadow and the street line's
     * own accent: 222 - 0.95·38 = 185.9 vs a shadow foot at 168 + 9 = 177.
     *
     * `streetBaselineY` carries rubric item 8 — the named street, plain, at the top of the
     * panel, at roughly one third the RÉSULTAT cap height.
     */
    banner: Object.freeze({
      x: 56, y: 34, w: 1808, h: 200, skewDeg: 8,
      titleSize: 122, titleBaselineY: 168,
      streetSize: 38, streetBaselineY: 222, streetIndent: 62,
      /** Right-hand count sits on the SAME two baselines as the title/street pair. */
      countSize: 74, countBaselineY: 168,
      subSize: 30, subBaselineY: 222,
      /**
       * Used only if `world` has not published a street name yet (harness cold start). Real
       * Plateau names, matching src/world/names.js, so a fallback still reads as Montréal.
       * The first entry that is not the banner's own street becomes the next-site teaser.
       */
      streetFallback: 'AV. DU MONT-ROYAL',
      nextFallbacks: Object.freeze(['RUE SAINT-DENIS', 'RUE RACHEL', 'AV. LAURIER']),
      /**
       * Height of the single bevel band along the very top of the banner. Round 6 filled the
       * top 30 % of the banner with white at 0.16, which put a hard horizontal seam straight
       * through the letterforms at mid-cap height. The band is now a thin edge highlight that
       * terminates well above the cap line.
       */
      bevelH: 10,
    }),
    body: Object.freeze({ x: 56, y: 250, w: 1808, h: 750 }),
    /** Everything inside the body panel, design px. */
    layout: Object.freeze({
      leftX: 112, colW: 780,
      tileW: 46, tileH: 50, tileGap: 7,

      /**
       * ---------------------------------------------------------------- hero cash plate
       * Rubric item 5: the cash figure must sit on its own HIGH-CHROMA accent plate, distinct
       * from the rest of the panel's plate colour. Round 6 failed it outright — the label got
       * the navy tile treatment and $3,184.50 was plain black type on white, so the hierarchy
       * was inverted (label dressed, number naked).
       *
       * It is now the reference §2.3/§2.4 marquee: ink border → cone-orange frame → cream tile
       * cells → navy digits, right-aligned, with a round lamp at each end. Cream-on-orange is
       * the only such plate in the frame, so it is unambiguously the loudest object after the
       * banner, and it introduces no hue the panel did not already carry.
       */
      cashTileY: 286,
      /**
       * ---------------------------------------------------------------- ONE GLYPH, ONE CELL
       * Round 10, measured at 4x: "the comma, the '1', the '5' and the '0' all cross their
       * dividers by 12-17 px". Cause: the plate drew its cells at one pitch and then set the
       * money as a SINGLE right-aligned run at the typeface's own advance, and the two never
       * agreed. At `cashCapH` 84 the run "$3,184.50" measures 427.6 px while nine cells of the
       * old 11-tile pitch span 585.8 — so every glyph drifted further left of its own cell than
       * the one before it, and the split-flap motif died on the hero object of the panel.
       *
       * The digits are now cell-set, one glyph per cell, exactly as `hud.js` already does on the
       * driving board, and the tile seams are re-laid ON TOP afterwards (§2.3: on a real
       * letterboard the joints are in front of the letters).
       *
       * Tile count therefore has a hard geometric constraint. Window width is
       * `colW - (bulbR*2 + 4)*2` = 780 - 68 = 712, so with `cashPlateSeam` 4:
       *
       *     tw(tiles) = (712 - 4*(tiles-1)) / tiles
       *
       * and the widest glyph in the face at cap height 84 is '8'/'0'/'3' at
       * (0.46 + weight 0.22) * 84 = 57.1 px. 11 tiles gives tw 61.1 (2.0 px of air per side —
       * inside the antialias, which is exactly what the critic photographed as a crossing);
       * 10 tiles gives tw 67.6 and 5.2 px per side, which survives the 4x zoom.
       *
       * "$" + "3,184.50" is 9 glyphs, so 10 cells leaves one blank cell at the left — a real
       * letterboard always has spare cells, and it is where the next digit rolls in.
       */
      cashPlateY: 342, cashPlateH: 124, cashPlateTiles: 10, cashPlateBulbR: 15,
      cashPlateSeam: 4,
      /** '$' is set a touch smaller than the digits: its own advance is the widest in the face. */
      cashSignScale: 0.86,
      cashNumY: 444, cashCapH: 84,

      scoreTileY: 498, scoreNumY: 622, scoreCapH: 72,
      /**
       * The money row is ONE right-aligned run: `$` then the numerals, set as a single block
       * whose right edge lands on `leftX + colW`. Drawing the sign left-aligned at the column
       * origin while the digits were right-aligned at its far edge is what opened the 195 px
       * hole between them (round-3 critic, fatal). `moneyGap` is the only space allowed
       * between sign and first digit, in cap-height units of the numerals.
       */
      moneyGap: 0.16,
      moneySignScale: 0.78,

      /**
       * Stat chips are TWO stacked rows inside the plate: label band on top, value baseline
       * below it. `chipValY` is measured from the chip's bottom edge, `chipLabelY` from its
       * top, so the two can never be pushed into each other by a size tweak.
       */
      chipY: 656, chipW: 244, chipH: 92, chipGap: 24,
      chipLabelSize: 23, chipLabelY: 30,
      chipValCapH: 40, chipValY: 14,

      classTileY: 772,
      stampX: 130, stampY: 830, stampS: 128,
      /**
       * Rank glyph size, as a fraction of the stamp plate. Measured on the round-10 frame:
       * at 0.82 the 'S' spans 105 design px inside a plate whose clear inner width is 119, but
       * its baseline sat at 0.34·S = 43.5 rather than at capH/2 = 52.5, so the glyph was 9 px
       * high of centre and its top overran the frame by 2 px — a pixel scan of the shipped PNG
       * found ONE contiguous orange run from x 201 to 248 at y = 824, i.e. glyph and frame
       * fused into a single blob at 4x. The baseline is now derived as capH/2, which centres
       * the glyph by construction, and 0.72 leaves 13 px of clear plate all round.
       */
      stampGlyphCap: 0.72,

      /**
       * Star rating (rubric criterion 4). Earned and unearned differ by FILL — gold face vs
       * white face — never by opacity. Sits in the band that was 570x140 of dead white beside
       * the CLASSE stamp, and replaces the orphaned grey lowercase "licence".
       */
      /**
       * `starX` was 306: the first star's left point reached x = 264 while the rotated stamp
       * plate's right-most orange pixel measured x = 267, so the two overlapped by 4 px and the
       * gold point read as growing out of the CLASSE frame. 326 puts a 15 px gutter between
       * them and the row still terminates at 326 + 4·104 + 42 = 784, clear of the AVANT card
       * at x = 1024.
       */
      starCount: 5, starX: 326, starY: 894, starR: 42, starGap: 104,

      beforeX: 1030, afterX: 1466, cardY: 300,

      /**
       * Completion badge. The whole PowerWash grammar hangs off this, so it is the largest
       * circular object in the frame: outer disc 2*(ringR+ringRim) = 284 design px = 15.7 % of
       * the body panel width, up from 10.6 % in round 3 when it read as third-loudest.
       */
      /**
       * The badge is ONE circle at ONE radius with ONE stroke width. `ringStartDeg` is the
       * canvas angle the sweep begins at: -90 is 12 o'clock, and it runs clockwise, so a 13 %
       * shortfall reads as a 47° notch at the top where the eye looks first. Round 6 started
       * the sweep at 121° (about 7 o'clock) around a 62° bottom gap, which meant the unfilled
       * remainder and the gap were two different things in the same channel and the whole
       * badge read as a rendering bug.
       *
       * `ringInk` is the channel outline: the SAME circle stroked wider in ink, drawn once,
       * under both the track and the fill. It is what makes the unfilled part read as an empty
       * section of the gauge instead of a stray crescent.
       */
      ringX: 1216, ringY: 782, ringR: 134, ringRim: 20, ringThickness: 38,
      ringStartDeg: -90, ringInk: 8,
      ringPctCapH: 92, ringPctDx: -22, ringPctDy: 32,
      ringPctSignSize: 46, ringPctSignDx: 50, ringPctSignDy: 4,
      ringLabelSize: 34, ringLabelDy: 46,
      compBarX: 1416, compBarY: 700, compBarW: 412,
      compBarLabelSize: 26, compCountCapH: 48, compCountDy: 78,

      /**
       * Fills the 510x140 dead white void the critic found below the completion bar, with the
       * next-objective teaser that was stranded in the footer prompt. Same two type systems as
       * the rest of the panel — stamped tile header, display label — so it adds no new plate.
       */
      nextTileY: 826, nextNameY: 918, nextNameSize: 46,

      promptY: 1046,
    }),
    stampShakePx: 16,
    stampRotDeg: -7,
    stampPopScale: 2.6,
    /**
     * Before/after thumbnail size on the payoff card.
     *
     * `seedOffset` is the difference between the AVANT and APRÈS seeds and it MUST be 0.
     * Round 10, at 4x: "the before/after thumbnails show two different pieces of road. They
     * must be the same patch, before and after — that is the entire payoff of the shot."
     * Round 6 passed `m.seed` and `m.seed + 7`, so the aggregate speckle, all five tar seams
     * and the lane-stripe shading differed between the two cards. `asphaltThumb` draws the
     * whole road base BEFORE it branches on mode, so an identical seed makes the two bases
     * bit-identical speck for speck, and the ONLY difference between the cards is the repair.
     *
     * `sawMargin` is how far the fresh patch oversails the hole it replaced, as a fraction of
     * the hole's own bounding box — a real saw-cut patch is squared off outside the broken
     * edge. The patch rectangle is now DERIVED from the same rim polygon the AVANT hole is
     * drawn from, so the two cards line up geometrically as well as texturally.
     */
    payoff: Object.freeze({ w: 380, h: 252, seedOffset: 0, sawMargin: 0.14 }),
    hazardStripe: 34,
  }),

  // ---------------------------------------------------------------- title / pause / countdown
  overlay: Object.freeze({
    titleSize: 178, subtitleSize: 46, promptSize: 40,
    countdownCapH: 300, countdownWeight: 0.26,
    goSize: 210,
    fade: 0.3,
  }),

  /** Pool caps — no per-frame allocation, ARCHITECTURE.md N9. */
  pools: Object.freeze({ callouts: 8, bonuses: 6 }),

  /** Announcer text bank. Keyed by style. Picked with the ui RNG fork, never Math.random. */
  lines: Object.freeze({
    combo: Object.freeze(['ENCHAÎNÉ!', 'ÇA ROULE!', 'BEAU TRAVAIL!', 'EN SÉRIE!']),
    perfect: Object.freeze(['PARFAIT!', 'IMPECCABLE!', 'DU BEAU BÉTON!']),
    repair: Object.freeze(['NID-DE-POULE!', 'BOUCHÉ!', 'RÉPARÉ!']),
    /**
     * `gameplay` already puts a world-space popText over the truck for a slalom chain, and it
     * used the literal string "THREADING THE NEEDLE". Carrying the same phrase in the corner
     * banner rendered it twice within 150 px in `night-rain`. The corner never speaks over a
     * live world-space callout now (see UiSystem._bindEvents), and the phrase is gone from
     * this bank so the two can never collide again.
     */
    slalom: Object.freeze(['SLALOM!', 'ENTRE LES CÔNES!', 'BIEN VISÉ!']),
    thump: Object.freeze(['AÏE!', 'OUCH — TROU OUVERT!', 'ÇA FAIT MAL']),
    break: Object.freeze(['COMBO PERDU', 'SÉRIE BRISÉE']),
  }),
});

export default TUNING;
